import { WaypointPosition } from "@/components/r3f/renderers/waypoint-label-render";
import { usePlaceholderElevation } from "@/hooks/use-walk-placeholder-positions";
import {
  cameraVecToScreenVec,
  eventToScreenVec,
  screenVecToCameraVec,
  worldVecToScreenVec,
} from "@/registration-tools/common/interaction/single-pin-interaction";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { selectShouldShowWaypointsOnFloors } from "@/store/view-options/view-options-selectors";
import {
  DEFAULT_CIRCLE_SIZE,
  UPDATE_CAMERA_MONITOR_PRIORITY,
  useThreeEventTarget,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericImgSheet,
  IElementImg360,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import { assert, CameraMonitor, memberWithPrivateData } from "@faro-lotv/lotv";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef, useState } from "react";
import { Box3, Camera, MOUSE, Vector2, Vector3 } from "three";

/** Number of pixel the mouse need to move between pointer down and up to consider it as a drag */
const POINTER_DRAG_THRESHOLD = 3;

type UsePickClosestWaypointProps = {
  /** The active camera */
  camera: Camera;

  /** Pano object of the scene, only available for panorama (SceneFilter.pano) */
  activePano?: IElementImg360;

  /** The sheet to use to place the placeholders */
  sheetForElevation?: IElementGenericImgSheet;

  /** The list of waypoints to consider */
  waypoints: WaypointPosition[];

  /** Callback when a waypoint is clicked */
  onWaypointClicked(id: IElementImg360): void;
};

/**
 * Hook that runs logic to jump to the closest waypoints when the user clicks on the screen.
 *
 * @returns The closest waypoint that would be selected on a click, or undefined if not available.
 * Should be used to highlight the waypoint in the UI.
 */
export function usePickClosestWaypoint({
  camera,
  activePano,
  sheetForElevation,
  waypoints,
  onWaypointClicked,
}: UsePickClosestWaypointProps): IElementImg360 | undefined {
  const [closestWaypoint, setClosestWaypoint] = useState<
    IElementImg360 | undefined
  >();
  const clickPos = useRef<Vector2>(undefined);

  const canvas = useThree((s) => s.gl.domElement);
  const domElement = useThreeEventTarget();

  const activeTool = useAppSelector(selectActiveTool);

  const shouldShowWaypointsOnFloor = useAppSelector(
    selectShouldShowWaypointsOnFloors,
  );

  const referenceElevation = usePlaceholderElevation(
    activePano,
    sheetForElevation,
    !shouldShowWaypointsOnFloor,
  );

  const waypointsOnScreen = useWaypointsOnScreen({
    camera,
    activePano,
    referenceElevation,
    waypoints,
  });

  // pointer down event handler
  // It will set clickPos so that pointer up event handler can check is mouse is dragged.
  // If closest waypoint is not set yet, it will also find and set the closest waypoint.
  const onPointerDown = useCallback(
    (event: PointerEvent) => {
      // It seems that in walk mode the target of the event is the canvas itself
      // while for split mode the target is the actual target DOM element
      if (
        (event.target !== domElement && event.target !== canvas) ||
        event.pointerType === "touch"
      ) {
        return;
      }
      clickPos.current = new Vector2(event.clientX, event.clientY);
      if (event.button === MOUSE.LEFT && !closestWaypoint) {
        setClosestWaypoint(
          findClosestWaypoint(
            camera,
            waypointsOnScreen,
            domElement,
            event,
            shouldShowWaypointsOnFloor,
            referenceElevation,
          ),
        );
      }
    },
    [
      canvas,
      camera,
      closestWaypoint,
      domElement,
      referenceElevation,
      shouldShowWaypointsOnFloor,
      waypointsOnScreen,
    ],
  );

  // pointer move event handler
  // It will find the waypoint that is closest to the pointer position and update closestWaypoint state.
  const onPointerMove = useCallback(
    (ev: PointerEvent) => {
      // when dragging, do not look for closest waypoint
      if (clickPos.current || ev.pointerType === "touch") return;

      // find the closest waypoint and update state if it is different from previous one
      const newClosestWaypoint = findClosestWaypoint(
        camera,
        waypointsOnScreen,
        domElement,
        ev,
        shouldShowWaypointsOnFloor,
        referenceElevation,
      );
      if (newClosestWaypoint !== closestWaypoint) {
        setClosestWaypoint(newClosestWaypoint);
      }
    },
    [
      camera,
      closestWaypoint,
      domElement,
      referenceElevation,
      shouldShowWaypointsOnFloor,
      waypointsOnScreen,
    ],
  );

  // pointer up event handler
  // For left click, it will activate the closest waypoint.
  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      if (
        // Taken by something else under the pointer, e.g. an AI finding's mask (see bounding-mask-mesh.tsx)
        event.defaultPrevented ||
        event.button !== MOUSE.LEFT ||
        event.pointerType === "touch" ||
        !closestWaypoint ||
        !clickPos.current ||
        clickPos.current.distanceTo(new Vector2(event.clientX, event.clientY)) >
          POINTER_DRAG_THRESHOLD
      ) {
        clickPos.current = undefined;
        return;
      }
      event.stopPropagation();
      clickPos.current = undefined;
      onWaypointClicked(closestWaypoint);
    },
    [closestWaypoint, onWaypointClicked],
  );

  useEffect(() => {
    if (activeTool || !activePano) return;

    domElement.addEventListener("pointermove", onPointerMove);
    domElement.addEventListener("pointerdown", onPointerDown);
    domElement.addEventListener("pointerup", onPointerUp);

    return () => {
      domElement.removeEventListener("pointermove", onPointerMove);
      domElement.removeEventListener("pointerup", onPointerUp);
      domElement.removeEventListener("pointerdown", onPointerDown);
    };
  }, [
    activeTool,
    closestWaypoint,
    domElement,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onWaypointClicked,
    activePano,
  ]);

  return closestWaypoint;
}

/**
 * The type to be used for quick waypoint navigation.
 * To improve performance, the array of the objects will be re-allocated and calculated each time when camera changes.
 */
type WaypointOnScreen = {
  /** The waypoint to show */
  pano: IElementImg360;

  /** The location in world space where waypoint/location marker will be rendered */
  renderPosition: Vector3;

  /** The 2D location of the waypoint on the screen (in pixel) */
  screenPos: Vector2;

  /** On screen placeholder radius squared in pixel when it facing camera (not used when shown on floors) */
  waypointScreenSize: number;

  /** The squared distance to camera (square meter) */
  distanceToCamera: number;
};

type UseWaypointsOnScreenProps = {
  /** The active camera */
  camera: Camera;

  /** Pano object of the scene, only available for panorama (SceneFilter.pano) */
  activePano?: IElementImg360;

  /** The reference elevation */
  referenceElevation?: number;

  /** The list of waypoints to consider */
  waypoints: WaypointPosition[];
};

/**
 * @returns A list of waypoints that are visible on screen and close to the given referenceElevation.
 */
function useWaypointsOnScreen({
  camera,
  activePano,
  referenceElevation,
  waypoints,
}: UseWaypointsOnScreenProps): WaypointOnScreen[] {
  const domElement = useThreeEventTarget();

  const activeTool = useAppSelector(selectActiveTool);

  const [waypointsOnScreen, setWaypointsOnScreen] = useState<
    WaypointOnScreen[]
  >([]);

  // Create a camera monitor to know when the camera is still
  const [cameraMonitor] = useState(() => new CameraMonitor());
  useFrame((_, delta) => {
    if (activePano) {
      cameraMonitor.checkCameraMovement(camera, delta);
    }
  }, UPDATE_CAMERA_MONITOR_PRIORITY);

  // Recalculates the waypoints on screen, when the camera stops moving.
  useEffect(() => {
    const cameraStopped = cameraMonitor.cameraStoppedMoving.on(() => {
      setWaypointsOnScreen(
        findWaypointsOnScreen(
          waypoints,
          camera,
          domElement,
          referenceElevation,
        ),
      );
    });
    return () => {
      cameraStopped.dispose();
    };
  }, [
    camera,
    cameraMonitor.cameraStoppedMoving,
    domElement,
    referenceElevation,
    waypoints,
  ]);

  useEffect(() => {
    if (activeTool || !activePano) return;

    setWaypointsOnScreen(
      findWaypointsOnScreen(waypoints, camera, domElement, referenceElevation),
    );
  }, [
    camera,
    domElement.clientWidth,
    domElement.clientHeight,
    waypoints,
    domElement,
    activeTool,
    referenceElevation,
    activePano,
  ]);

  return waypointsOnScreen;
}

const findClosestWaypoint = memberWithPrivateData(() => {
  const tempVecThree1 = new Vector3();
  const tempVecThree2 = new Vector3();
  const tempVecThree3 = new Vector3();

  const tempVecThree4 = new Vector2();
  const tempVecThree5 = new Vector2();

  return (
    camera: Camera,
    waypointsOnScreen: WaypointOnScreen[],
    domElement: HTMLElement,
    ev: PointerEvent,
    shouldShowWaypointsOnFloor: boolean,
    referenceElevation?: number,
  ): IElementImg360 | undefined => {
    if (waypointsOnScreen.length === 0) return;

    const screenVec = eventToScreenVec(domElement, ev, tempVecThree1);
    if (shouldShowWaypointsOnFloor && referenceElevation) {
      // raycast with the hypothetical level plane at referenceElevation to get the hit point
      // then find the waypoint that is closest to the hit point

      // calculate cursor coordinate in camera's Normalized Device Coordinate (NDC) on near plane
      const cameraVec1 = screenVecToCameraVec(
        screenVec,
        tempVecThree2,
        domElement.clientWidth,
        domElement.clientHeight,
      );

      // get cursor coordinate in camera's Normalized Device Coordinate (NDC) on far plane and un-project it o world space
      const cameraVec2 = tempVecThree3
        .copy(cameraVec1)
        .setZ(1)
        .unproject(camera);

      // un-project cameraVec1 to world space
      cameraVec1.unproject(camera);

      // vector from cameraVec1 to cameraVec2 in world space
      cameraVec2.sub(cameraVec1);

      // raycast line is: (x, y, z)=cameraVec1 + k*cameraVec2
      // For the line segment within camera's frustum, k is in the range between 0 and 1.
      // We need find the intersection between this line and the hypothetical floor plane at referenceElevation

      // if the line vector is parallel to the level plane, there is no intersection (thus no closest waypoint)
      if (cameraVec2.y === 0) return;

      const k = (referenceElevation - cameraVec1.y) / cameraVec2.y;
      // if the intersection is out of camera's frustum, do not search for closest waypoint
      if (k <= 0 || k >= 1) return;

      // get the intersection point on the hypothetical level plane at referenceElevation
      cameraVec1.addScaledVector(cameraVec2, k);

      // Project all candidate waypoint positions on to the level plane and find the one
      // closest to cameraVec1, i.e. use (X, Z) 2D coordinate for distance calculation
      const refPos = tempVecThree4.set(cameraVec1.x, cameraVec1.z);

      // find the waypoint closest to refPos in in X-Z plane
      let candidateIndex = 0;
      let minDistance = tempVecThree5
        .set(
          waypointsOnScreen[0].renderPosition.x,
          waypointsOnScreen[0].renderPosition.z,
        )
        .distanceToSquared(refPos);
      for (let i = 1; i < waypointsOnScreen.length; i++) {
        const distanceToCursor = tempVecThree5
          .set(
            waypointsOnScreen[i].renderPosition.x,
            waypointsOnScreen[i].renderPosition.z,
          )
          .distanceToSquared(refPos);
        if (distanceToCursor < minDistance) {
          minDistance = distanceToCursor;
          candidateIndex = i;
        }
      }
      return waypointsOnScreen[candidateIndex].pano;
    }

    // calculate distance between waypoint position on screen and the cursor position in 2D screen space
    // and find the one that is closest to the cursor
    const cursorScreenPos = tempVecThree4.set(screenVec.x, screenVec.y);

    let candidateIndex = 0;
    let minDistance =
      waypointsOnScreen[0].screenPos.distanceToSquared(cursorScreenPos);
    for (let i = 1; i < waypointsOnScreen.length; i++) {
      const distanceToCursor =
        waypointsOnScreen[i].screenPos.distanceToSquared(cursorScreenPos);
      if (distanceToCursor < minDistance) {
        minDistance = distanceToCursor;
        candidateIndex = i;
      }
    }

    // if waypoints are shown on floors, simply return the one that is closest to the cursor regardless overlapping
    if (shouldShowWaypointsOnFloor) {
      return waypointsOnScreen[candidateIndex].pano;
    }

    // If waypoints are not projected on floors, there is high chance that multiple waypoints
    // will overlap on the screen. In this case, we need search the one on top out of the
    // overlapped waypoints (i.e. the one closest to camera)
    let refDistance = waypointsOnScreen[candidateIndex].distanceToCamera;
    let newCandidateIndex = candidateIndex;
    for (let i = 0; i < waypointsOnScreen.length; i++) {
      if (waypointsOnScreen[i].distanceToCamera < refDistance) {
        if (
          waypointsOnScreen[candidateIndex].screenPos.distanceToSquared(
            waypointsOnScreen[i].screenPos,
          ) <= waypointsOnScreen[i].waypointScreenSize
        ) {
          refDistance = waypointsOnScreen[i].distanceToCamera;
          newCandidateIndex = i;
        }
      }
    }

    return waypointsOnScreen[newCandidateIndex].pano;
  };
});

/**  Threshold to consider a waypoint is close in elevation to a reference value (in meter) */
const ELEVATION_RANGE = 1.25;

/**
 * find the waypoints that are located in the camera's frustum and close to give elevation
 * calculate their on-screen info
 *
 * @param waypoints complete waypoint list to be rendered
 * @param camera the given camera
 * @param domElement The HTML element to calculate the position relative to
 * @param referenceElevation the given reference elevation
 * @returns list of on-screen waypoints
 */
function findWaypointsOnScreen(
  waypoints: WaypointPosition[],
  camera: Camera,
  domElement: HTMLElement,
  referenceElevation?: number,
): WaypointOnScreen[] {
  const TEMP = new Vector3();

  // camera's Normalized Device Coordinate (NDC) box
  const box = new Box3(new Vector3(-1, -1, 0), new Vector3(1, 1, 1));
  const cameraPos = camera.getWorldPosition(new Vector3());

  // When waypoints are rendered facing camera, the waypoint circles are located on planes
  // that are orthogonal to camera direction vector. Need to find the vector in order to find
  // a point on the circle edge and calculate the waypoint size on screen
  const cameraDirection = camera.getWorldDirection(new Vector3());
  const orthVector = new Vector3(cameraDirection.z, 0, -cameraDirection.x);
  if (Math.abs(cameraDirection.x) < Math.abs(cameraDirection.y)) {
    orthVector.set(0, cameraDirection.z, -cameraDirection.y);
  }
  orthVector.normalize().multiplyScalar(DEFAULT_CIRCLE_SIZE);

  const waypointsOnScreen = new Array<WaypointOnScreen>();
  waypoints.forEach((waypoint) => {
    TEMP.copy(waypoint.renderPosition).project(camera);
    if (
      box.containsPoint(TEMP) &&
      (!referenceElevation ||
        Math.abs(waypoint.renderPosition.y - referenceElevation) <
          ELEVATION_RANGE)
    ) {
      cameraVecToScreenVec(
        TEMP,
        TEMP,
        domElement.clientWidth,
        domElement.clientHeight,
      );
      const screenPos = new Vector2(TEMP.x, TEMP.y);
      const distanceToCamera =
        waypoint.renderPosition.distanceToSquared(cameraPos);

      // an edge point of the waypoint circle when facing camera
      const edgePoint = orthVector.clone().add(waypoint.renderPosition);

      // find the screen position of the edge point
      worldVecToScreenVec(
        edgePoint,
        TEMP,
        camera,
        domElement.clientWidth,
        domElement.clientHeight,
      );

      // calculate way point screen radius square
      const waypointScreenSize = screenPos.distanceToSquared(
        new Vector2(TEMP.x, TEMP.y),
      );

      assert(isIElementImg360(waypoint.placeHolder));
      waypointsOnScreen.push({
        pano: waypoint.placeHolder,
        renderPosition: waypoint.renderPosition,
        distanceToCamera,
        screenPos,
        waypointScreenSize,
      });
    }
  });

  return waypointsOnScreen;
}
